// src/components/digital_twin/SimulationRunHistory.js
// FlowSense MOW+ — recent simulation runs on the server (any operator, last 6 h). Click to re-open a
// run on the twin (useSimulation.loadRun) — no re-computation.
import React, { memo, useCallback, useEffect, useState } from 'react';
import { Badge, Box, Flex, HStack, IconButton, Spinner, Text, VStack, useColorModeValue } from '@chakra-ui/react';
import { FaHistory, FaSync } from 'react-icons/fa';
import { listSimulationRuns } from '../../services/twinSimulationApi';

const ago = (iso) => {
  const s = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (!Number.isFinite(s)) return '';
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
};

function SimulationRunHistory({ onOpen, refreshKey, activeRunId }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const muted = useColorModeValue('gray.500', 'gray.400');
  const hover = useColorModeValue('gray.50', 'whiteAlpha.100');
  const active = useColorModeValue('purple.50', 'rgba(128,90,213,0.15)');

  const load = useCallback(async () => {
    setLoading(true);
    try { setRuns(await listSimulationRuns()); } catch (_) { /* list is optional */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (!runs.length && !loading) return null;
  return (
    <Box>
      <Flex justify="space-between" align="center" mb={1.5}>
        <HStack spacing={1.5}><FaHistory size={10} /><Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color={muted}>Recent runs</Text></HStack>
        <IconButton size="xs" variant="ghost" aria-label="Refresh runs" icon={loading ? <Spinner size="xs" /> : <FaSync />} onClick={load} />
      </Flex>
      <VStack align="stretch" spacing={0.5}>
        {runs.slice(0, 8).map((r) => {
          const s = r.summary || {};
          const headline = s.assetsFlooded != null ? `${s.assetsFlooded} ponding/flooded` : s.moderateOrWorse != null ? `${s.moderateOrWorse} damaged` : `${r.affected_count ?? 0} affected`;
          return (
            <Flex key={r.run_id} as="button" type="button" textAlign="left" px={2} py={1.5} borderRadius="md" align="center" gap={2}
              bg={r.run_id === activeRunId ? active : undefined} _hover={{ bg: hover }} onClick={() => onOpen?.(r.run_id)}>
              <Box flex={1} minW={0}>
                <Text fontSize="11px" fontWeight="700" noOfLines={1}>{r.title}</Text>
                <Text fontSize="9px" color={muted} noOfLines={1}>{headline} · {r.created_by?.name || 'operator'} · {ago(r.created_at)}</Text>
              </Box>
              {r.mode && <Badge fontSize="8px" variant="outline">{r.mode}</Badge>}
            </Flex>
          );
        })}
      </VStack>
    </Box>
  );
}

export default memo(SimulationRunHistory);
